import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useApi } from "@/api/hooks/useApi";
import type { RateMessageDto } from "@/api/types";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { QUERY_KEYS } from "@/query-keys";
import { useTranslation } from "@/translations";

const useRateMessage = () => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      conversationId,
      messageId,
      data,
    }: {
      conversationId: string;
      messageId: string;
      data: RateMessageDto;
    }) => api.postAssistantConversationsMessagesRateV1(conversationId, messageId, data),
    onSuccess: (_) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ASSISTANT_CONVERSATIONS });
    },
  });
};

const useRateConversation = () => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ conversationId, data }: { conversationId: string; data: RateMessageDto }) =>
      api.postAssistantConversationsRateV1(conversationId, data),
    onSuccess: (_, { conversationId }) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ASSISTANT_CONVERSATION(conversationId) });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ASSISTANT_CONVERSATIONS_LIST() });
    },
  });
};

const useDeleteConversation = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const showFlashToast = useFlashToast();

  return useMutation({
    mutationFn: (conversationId: string) => api.deleteAssistantConversationsByIdV1(conversationId),
    onSuccess: async (_, conversationId) => {
      queryClient.removeQueries({ queryKey: QUERY_KEYS.ASSISTANT_CONVERSATION(conversationId) });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ASSISTANT_CONVERSATIONS_LIST() });
    },
    onError: () => {
      showFlashToast({
        variant: "alert",
        message: t("page.ask-nobi-chat.conversation-sidebar.delete.notification.error"),
      });
    },
  });
};

export const askNobiChatMutations = { useRateMessage, useRateConversation, useDeleteConversation };
